import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useGetRechargeReceptionAddressesQuery } from '../../store/api/balanceApi';
import { ButtonSpinner } from '../ui/Loader';

interface ShamCashClaimFormProps {
  onSubmit: (transactionId: string, amount: number, currency: 'USD' | 'SYP') => Promise<void>;
  isLoading?: boolean;
}

export function ShamCashClaimForm({ onSubmit, isLoading = false }: ShamCashClaimFormProps) {
  const { t } = useTranslation();
  const [transactionId, setTransactionId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<'USD' | 'SYP'>('USD');
  const [error, setError] = useState<string | null>(null);

  const { data: receptionAddressesResponse, isFetching: isFetchingReceptionData } =
    useGetRechargeReceptionAddressesQuery();

  const shamCashData = receptionAddressesResponse?.data.shamCash;
  const receptionNumber = shamCashData?.receptionNumber?.trim() || '';
  const minAmount = shamCashData?.minAmount ?? 0.1;

  const exchangeRate = useMemo(() => {
    const rate = receptionAddressesResponse?.data.usdToSypExchangeRate;
    return typeof rate === 'number' && rate > 0 ? rate : 10000;
  }, [receptionAddressesResponse]);

  const handleTransactionIdChange = (value: string) => {
    const onlyDigits = value.replace(/\D/g, '');
    setTransactionId(onlyDigits);
    setError(null);
  };

  const handleAmountChange = (value: string) => {
    const filtered = value.replace(/[^0-9.]/g, '');
    setAmount(filtered);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      setError(t('recharge.shamCashClaim.amountRequired'));
      return;
    }

    if (!transactionId.trim()) {
      setError(t('recharge.shamCashClaim.transactionIdRequired'));
      return;
    }

    setError(null);
    await onSubmit(transactionId.trim(), parseFloat(amount), currency);
  };

  const handleCopyReceptionNumber = async () => {
    if (!receptionNumber) return;
    try {
      await navigator.clipboard.writeText(receptionNumber);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError'));
    }
  };

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6" noValidate>
      <div className="bg-neutral-dark/30 rounded-lg p-4">
        <p className="text-sm text-neutral-medium font-body">
          {t('recharge.shamCashClaim.instructions')}
        </p>
      </div>

      <div className="bg-neutral-dark/30 rounded-lg p-4 space-y-3">
        <h3 className="text-base font-heading font-semibold text-brand-accent">
          {t('recharge.shamCashClaim.receptionTitle')}
        </h3>

        <div className="space-y-2">
          <span className="text-neutral-medium font-body text-sm block">
            {t('recharge.invoice.receptionNumber')}:
          </span>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-2 bg-neutral-dark/50 rounded-lg">
            <span className="text-neutral-light font-mono text-sm break-all">
              {isFetchingReceptionData
                ? t('common.loading')
                : receptionNumber || t('recharge.shamCashClaim.notAvailable')}
            </span>
            {!isFetchingReceptionData && receptionNumber ? (
              <button
                type="button"
                onClick={() => void handleCopyReceptionNumber()}
                className="p-1.5 bg-brand-accent/20 text-brand-accent rounded hover:bg-brand-accent/30 transition-colors flex-shrink-0 self-end sm:self-auto"
                title={t('common.copy')}
                aria-label={t('common.copy')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <span className="text-neutral-medium font-body text-sm">{t('recharge.currency')}:</span>
          <span className="text-neutral-light font-body text-sm">
            {(shamCashData?.acceptedCurrencies?.length
              ? shamCashData.acceptedCurrencies.join(' / ')
              : 'USD / SYP')}
          </span>
        </div>

        <p className="text-xs text-neutral-medium font-body">
          {t('recharge.exchangeRate.info', { rate: exchangeRate.toLocaleString() })}
        </p>
        <p className="text-xs text-neutral-medium font-body">
          {minAmount > 0
            ? `${t('recharge.minAmount')}: ${minAmount} USD (${t('recharge.exchangeRate.equivalent', { amount: minAmount * exchangeRate, currency: 'SYP' })})`
            : t('recharge.noMinAmount')}
        </p>
      </div>

      {/* Currency Selection */}
      <div>
        <label className="block text-sm font-medium text-neutral-light mb-2 font-body">
          {t('recharge.shamCashClaim.currency')} <span className="text-danger">*</span>
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setCurrency('USD')}
            className={`flex-1 px-4 py-3 rounded-lg font-heading font-semibold transition-all duration-300 ${
              currency === 'USD'
                ? 'bg-brand-accent text-brand-primary'
                : 'card-glass text-neutral-light hover:border-brand-accent/50'
            }`}
          >
            🇺🇸 {t('recharge.shamCashClaim.currencyUSD')}
          </button>
          <button
            type="button"
            onClick={() => setCurrency('SYP')}
            className={`flex-1 px-4 py-3 rounded-lg font-heading font-semibold transition-all duration-300 ${
              currency === 'SYP'
                ? 'bg-brand-accent text-brand-primary'
                : 'card-glass text-neutral-light hover:border-brand-accent/50'
            }`}
          >
            🇸🇾 {t('recharge.shamCashClaim.currencySYP')}
          </button>
        </div>
      </div>

      {/* Amount Input */}
      <div>
        <label
          htmlFor="shamCashAmount"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('recharge.shamCashClaim.amount')} <span className="text-danger">*</span>
        </label>
        <input
          id="shamCashAmount"
          name="amount"
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => handleAmountChange(e.target.value)}
          disabled={isLoading}
          placeholder={t('recharge.shamCashClaim.amountPlaceholder')}
          className="w-full px-4 py-3 card-glass rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body"
        />
      </div>

      {/* Transaction ID Input */}
      <div>
        <label
          htmlFor="shamCashTransactionId"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('recharge.shamCashClaim.transactionId')} <span className="text-danger">*</span>
        </label>
        <input
          id="shamCashTransactionId"
          name="transactionId"
          type="text"
          inputMode="numeric"
          value={transactionId}
          onChange={(e) => handleTransactionIdChange(e.target.value)}
          disabled={isLoading}
          placeholder={t('recharge.shamCashClaim.transactionIdPlaceholder')}
          className={`w-full px-4 py-3 card-glass rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${
            error ? 'border-danger' : 'border-neutral-dark'
          }`}
        />
        {error && (
          <p className="mt-1 text-sm text-danger font-body animate-slide-down">{error}</p>
        )}
        <p className="mt-1 text-xs text-neutral-medium font-body">
          {t('recharge.shamCashClaim.transactionIdHint')}
        </p>
      </div>

      <button
        type="submit"
        disabled={isLoading || !transactionId.trim() || !amount || parseFloat(amount) <= 0}
        className="w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <ButtonSpinner />
            <span>{t('common.loading')}</span>
          </>
        ) : (
          t('recharge.shamCashClaim.submit')
        )}
      </button>
    </form>
  );
}
